import React, { useCallback, useEffect, useState } from 'react';
import { Box, Skeleton, Button, Divider, Stack, TextField, Typography } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useClinicSettings } from './hooks/useClinicSettings';
import Loading from '../../components/loading';
import SuccessMessage from '../../components/successMessage';
import ErrorMessage from '../../components/errorMessage';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

export default function GeneralData() {
  const navigate = useNavigate();
  const { fetchClinic, saveClinic, fetchLogoThumbnail, uploadLogo } = useClinicSettings();

  const [isLoading, setLoading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [logoPath, setLogoPath] = useState('');
  // Config owned by the clinic aggregate but edited on the "Configurações Gerais" screen. Loaded and
  // re-sent as-is here so the full-overwrite PUT /clinic doesn't wipe them.
  const [usesInsurance, setUsesInsurance] = useState(false);
  const [useReminderMessageWhatsAppForPatient, setUseReminderMessageWhatsAppForPatient] =
    useState(true);
  const [useAgendaRoom, setUseAgendaRoom] = useState(false);
  const [logoThumbnail, setLogoThumbnail] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [isLogoLoading, setLogoLoading] = useState(true);

  const getClinic = useCallback(() => {
    setLoading(true);
    fetchClinic()
      .then((res) => {
        if (res.data) {
          setName(res.data.name ?? '');
          setAddress(res.data.address ?? '');
          setCity(res.data.city ?? '');
          setPhone(res.data.phone ?? '');
          setEmail(res.data.email ?? '');
          setLogoPath(res.data.logoPath ?? '');
          setUsesInsurance(res.data.usesInsurance ?? false);
          setUseReminderMessageWhatsAppForPatient(
            res.data.useReminderMessageWhatsAppForPatient ?? true,
          );
          setUseAgendaRoom(res.data.useAgendaRoom ?? false);
        }
      })
      .catch((err) => {
        if (err.status === 401) navigate('/expired-session');
        setShowErrorMessage(true);
        setErrorMessage('Erro ao carregar dados da clínica.');
      })
      .finally(() => setLoading(false));
  }, [navigate, fetchClinic]);

  const getThumbnail = useCallback(() => {
    setLogoLoading(true);
    fetchLogoThumbnail()
      .then((res) => setLogoThumbnail(res.data))
      .catch(() => {})
      .finally(() => setLogoLoading(false));
  }, [fetchLogoThumbnail]);

  useEffect(() => {
    getClinic();
    getThumbnail();
  }, [getClinic, getThumbnail]);

  const handleLogoFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const MAX_SIZE_BYTES = 5 * 1024 * 1024;
    if (file.size > MAX_SIZE_BYTES) {
      setShowErrorMessage(true);
      setErrorMessage('A imagem excede o limite de 5 MB.');
      event.target.value = '';
      return;
    }

    if (!file.type.startsWith('image/')) {
      setShowErrorMessage(true);
      setErrorMessage('Selecione um arquivo de imagem válido (JPG, PNG, etc.).');
      event.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => setLogoPreview(reader.result);
    reader.readAsDataURL(file);

    const formData = new FormData();
    formData.append('file', file);
    setLoading(true);
    uploadLogo(formData)
      .then((res) => {
        setLogoPath(res.data);
        setLogoPreview(null);
        getThumbnail();
        setShowSuccessMessage(true);
        setSuccessMessage('Logo enviada com sucesso!');
      })
      .catch((err) => {
        if (err.status === 401) navigate('/expired-session');
        setShowErrorMessage(true);
        setErrorMessage(err.response?.data ?? 'Erro ao enviar a logo.');
        setLogoPreview(null);
      })
      .finally(() => setLoading(false));
  };

  const handleSave = () => {
    setLoading(true);
    setShowSuccessMessage(false);
    setShowErrorMessage(false);

    saveClinic({
      name,
      address,
      city,
      email,
      phone,
      logoPath,
      usesInsurance,
      useReminderMessageWhatsAppForPatient,
      useAgendaRoom,
    })
      .then(() => {
        setShowSuccessMessage(true);
        setSuccessMessage('Dados da clínica salvos com sucesso!');
      })
      .catch((err) => {
        if (err.status === 401) navigate('/expired-session');
        setShowErrorMessage(true);
        setErrorMessage(err.response?.data ?? 'Erro ao salvar dados da clínica.');
      })
      .finally(() => setLoading(false));
  };

  return (
    <>
      <Loading isToHidden={!isLoading} />
      {showSuccessMessage && (
        <SuccessMessage
          message={successMessage}
          show={showSuccessMessage}
          setShow={setShowSuccessMessage}
        />
      )}
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      <Stack spacing={3}>
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            p: 2,
          }}
        >
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}
          >
            Dados da Clínica
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Stack spacing={2}>
            <TextField
              label="Nome"
              size="small"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
            />
            <TextField
              label="Endereço"
              size="small"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              fullWidth
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Cidade"
                size="small"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                fullWidth
              />
              <TextField
                label="Telefone"
                size="small"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                fullWidth
              />
              <TextField
                label="E-mail"
                size="small"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
              />
            </Stack>
          </Stack>
        </Box>

        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            p: 2,
          }}
        >
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}
          >
            Logo da Clínica
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
            <Box sx={{ textAlign: 'center' }}>
              {isLogoLoading ? (
                <Skeleton variant="rounded" width={120} height={120} />
              ) : logoPreview || logoThumbnail ? (
                <Box
                  component="img"
                  src={logoPreview || logoThumbnail}
                  alt="Logo da clínica"
                  loading="lazy"
                  sx={{
                    width: 120,
                    height: 120,
                    borderRadius: 1,
                    objectFit: 'cover',
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                  }}
                />
              ) : null}
              {logoPreview && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5, display: 'block' }}
                >
                  Enviando...
                </Typography>
              )}
            </Box>
            <Stack spacing={1}>
              <Button
                component="label"
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                size="small"
              >
                {logoThumbnail || logoPreview ? 'Alterar Logo' : 'Selecionar Logo'}
                <VisuallyHiddenInput
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  onChange={handleLogoFileChange}
                />
              </Button>
              <Typography variant="caption" color="text.secondary">
                JPG, PNG, GIF ou WebP — máx. 5 MB
              </Typography>
            </Stack>
          </Stack>
        </Box>

        <Box>
          <Button variant="contained" onClick={handleSave}>
            Salvar
          </Button>
        </Box>
      </Stack>
    </>
  );
}
